/**
 * StreamingVideoLoader - HLS streaming support with fallback to progressive MP4
 *
 * Supports:
 * - Native HLS on iOS/Safari
 * - hls.js polyfill on Chrome/Firefox/Edge
 * - Automatic fallback to progressive MP4 if HLS fails
 */

import { Logger } from '../utils/Logger';
import Hls, { HlsConfig } from 'hls.js';
import { DeviceDetector } from '../utils/DeviceDetector';

const logger = Logger.getLogger('StreamingVideoLoader');

export interface StreamingConfig {
  /** Maximum buffer length in seconds (default: 30 for desktop, 20 for mobile) */
  maxBufferLength?: number;
  /** Maximum buffer size in bytes (default: 60MB for desktop, 40MB for mobile) */
  maxBufferSize?: number;
  /** Starting quality level (-1 = auto) */
  startLevel?: number;
  /** Cap quality to player size on mobile */
  capLevelToPlayerSize?: boolean;
}

export interface QualityLevel {
  height: number;
  width: number;
  bitrate: number;
  name?: string;
}

export class StreamingVideoLoader {
  private hls: Hls | null = null;
  private video: HTMLVideoElement | null = null;

  /**
   * Check if browser supports native HLS (Safari/iOS)
   */
  static supportsNativeHLS(): boolean {
    const video = document.createElement('video');
    return video.canPlayType('application/vnd.apple.mpegurl') !== '';
  }

  /**
   * Check if hls.js is supported (MSE-based browsers)
   */
  static supportsHLSJS(): boolean {
    return Hls.isSupported();
  }

  /**
   * Check if any form of HLS is supported
   */
  static supportsHLS(): boolean {
    return this.supportsNativeHLS() || this.supportsHLSJS();
  }

  /**
   * Load HLS stream into video element
   * @param hlsUrl - URL to .m3u8 manifest (can be any HLS source - your Mux, user's Mux, etc.)
   * @param video - HTMLVideoElement to attach stream to
   * @param config - Optional streaming configuration
   */
  async loadHLS(
    hlsUrl: string,
    video: HTMLVideoElement,
    config?: StreamingConfig
  ): Promise<void> {
    this.video = video;
    const isMobile = DeviceDetector.isMobile();

    // Build hls.js config with device-aware defaults
    const hlsConfig: Partial<HlsConfig> = {
      // Buffer settings - smaller on mobile for memory
      maxBufferLength: config?.maxBufferLength ?? (isMobile ? 20 : 30),
      maxMaxBufferLength: isMobile ? 40 : 60,
      maxBufferSize: config?.maxBufferSize ?? (isMobile ? 40_000_000 : 60_000_000),

      // Quality selection
      startLevel: config?.startLevel ?? -1, // Auto-select initial quality
      capLevelToPlayerSize: config?.capLevelToPlayerSize ?? isMobile, // Don't load 4K on phone

      // ABR (Adaptive Bitrate) settings
      abrEwmaDefaultEstimate: 5_000_000, // Start with 5Mbps estimate
      abrBandWidthFactor: 0.95, // Conservative bandwidth usage
      abrBandWidthUpFactor: 0.7, // Slower quality upgrades

      // Low latency settings (good for VR)
      lowLatencyMode: false, // Not needed for VOD content
      backBufferLength: 30, // Keep 30s of back buffer for seeking
    };

    // iOS/Safari - use native HLS
    if (StreamingVideoLoader.supportsNativeHLS()) {
      logger.info('Using native HLS (Safari/iOS)');
      logger.debug(`Stream URL: ${hlsUrl}`);
      video.src = hlsUrl;
      return this.waitForVideoReady(video);
    }

    // Chrome/Firefox/Edge - use hls.js
    if (StreamingVideoLoader.supportsHLSJS()) {
      logger.info('Using hls.js polyfill (Chrome/Firefox/Edge)');
      logger.debug(`Stream URL: ${hlsUrl}`);

      this.hls = new Hls(hlsConfig);
      this.hls.loadSource(hlsUrl);
      this.hls.attachMedia(video);

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('HLS manifest load timeout (15s)'));
        }, 15000);

        this.hls!.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
          clearTimeout(timeoutId);
          logger.info(`HLS manifest loaded: ${data.levels.length} quality levels`);
          resolve();
        });

        this.hls!.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            clearTimeout(timeoutId);
            logger.error('Fatal HLS error:', data.type, data.details);
            reject(new Error(`HLS Error: ${data.type} - ${data.details}`));
          } else {
            logger.warn('Non-fatal HLS error:', data.type, data.details);
          }
        });
      });
    }

    throw new Error('HLS not supported on this browser');
  }

  /**
   * Wait for video element to be ready to play
   */
  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Video ready timeout (15s)'));
      }, 15000);

      const onReady = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('loadedmetadata', onReady);
        video.removeEventListener('error', onError);
        logger.debug(`Video ready: ${video.videoWidth}x${video.videoHeight}`);
        resolve();
      };

      const onError = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('loadedmetadata', onReady);
        video.removeEventListener('error', onError);
        reject(video.error || new Error('Video load error'));
      };

      video.addEventListener('loadedmetadata', onReady, { once: true });
      video.addEventListener('error', onError, { once: true });

      // If already ready (cached), resolve immediately
      if (video.readyState >= 1) {
        onReady();
      }
    });
  }

  /**
   * Get current quality level index
   * Returns -1 if auto or not using hls.js
   */
  getCurrentLevel(): number {
    return this.hls?.currentLevel ?? -1;
  }

  /**
   * Get all available quality levels
   */
  getLevels(): QualityLevel[] {
    if (!this.hls) return [];

    return this.hls.levels.map((level) => ({
      height: level.height,
      width: level.width,
      bitrate: level.bitrate,
      name: `${level.height}p`,
    }));
  }

  /**
   * Manually set quality level
   * @param level - Level index, or -1 for auto
   */
  setLevel(level: number): void {
    if (this.hls) {
      this.hls.currentLevel = level;
      logger.debug(`Quality level set to: ${level === -1 ? 'auto' : level}`);
    }
  }

  /**
   * Get buffer status
   */
  getBufferInfo(): { buffered: number; duration: number; percent: number } {
    if (!this.video) {
      return { buffered: 0, duration: 0, percent: 0 };
    }

    const duration = this.video.duration || 0;
    let buffered = 0;

    if (this.video.buffered.length > 0) {
      buffered = this.video.buffered.end(this.video.buffered.length - 1);
    }

    return {
      buffered,
      duration,
      percent: duration > 0 ? (buffered / duration) * 100 : 0,
    };
  }

  /**
   * Check if this is using hls.js (vs native HLS)
   */
  isUsingHLSJS(): boolean {
    return this.hls !== null;
  }

  /**
   * Dispose of HLS instance and clean up resources
   */
  dispose(): void {
    if (this.hls) {
      logger.debug('Disposing hls.js instance');
      this.hls.destroy();
      this.hls = null;
    }
    this.video = null;
  }
}
